import React from 'react';
import { getPlatformColor, formatValue } from './utils/platformUtils';

interface ChannelAnalytics {
  [key: string]: any;
}

interface PlatformAnalyticsCardProps {
  channel: string;
  postId: string;
  analytics?: ChannelAnalytics;
  isLoading: boolean;
}

const PlatformAnalyticsCard: React.FC<PlatformAnalyticsCardProps> = ({
  channel,
  analytics,
  isLoading,
}) => {
  return (
    <div className="space-y-3">
      {/* Platform Name */}
      <h3
        style={{ color: getPlatformColor(channel) }}
        className="text-md font-medium capitalize"
      >
        {channel.replace('-', ' ')}
      </h3>

      {/* Stats Card */}
      <div className="bg-[#1a1a1a] rounded-lg p-4 border border-gray-800">
        {isLoading ? (
          /* Skeleton Loading */
          <div className="grid grid-cols-4 gap-4 animate-pulse">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex flex-col items-center">
                <div className="h-6 w-12 bg-gray-700 rounded mb-2"></div>
                <div className="h-4 w-16 bg-gray-700 rounded"></div>
              </div>
            ))}
          </div>
        ) : analytics ? (
          /* Analytics Data */
          channel.toLowerCase() === 'email' ? (
            /* Email-specific metrics */
            <div className="grid grid-cols-5 gap-4">
              {/* Sent */}
              <div className="flex flex-col items-center">
                <div className="text-white text-lg font-semibold">
                  {formatValue(analytics.requests || 0)}
                </div>
                <div className="text-gray-400 text-xs mt-2 tracking-wide">
                  Sent
                </div>
              </div>

              {/* Delivered */}
              <div className="flex flex-col items-center">
                <div className="text-white text-lg font-semibold">
                  {formatValue(analytics.delivered || 0)}
                </div>
                <div className="text-gray-400 text-xs mt-2 tracking-wide">
                  Delivered
                </div>
              </div>

              {/* Opened */}
              <div className="flex flex-col items-center">
                <div className="text-white text-lg font-semibold">
                  {formatValue(analytics.opens || analytics.unique_opens || 0)}
                </div>
                <div className="text-gray-400 text-xs mt-2 tracking-wide">
                  Opened
                </div>
              </div>

              {/* Clicked */}
              <div className="flex flex-col items-center">
                <div className="text-white text-lg font-semibold">
                  {formatValue(analytics.clicks || analytics.unique_clicks || 0)}
                </div>
                <div className="text-gray-400 text-xs mt-2 tracking-wide">
                  Clicked
                </div>
              </div>

              {/* Failed */}
              <div className="flex flex-col items-center">
                <div className="text-white text-lg font-semibold">
                  {formatValue(analytics.bounces || 0)}
                </div>
                <div className="text-gray-400 text-xs mt-2 tracking-wide">
                  Failed
                </div>
              </div>
            </div>
          ) : channel.toLowerCase() === 'instagram' ? (
            /* Instagram-specific metrics */
            <div className="grid grid-cols-3 gap-4">
              {/* Views */}
              <div className="flex flex-col items-center">
                <div className="text-white text-lg font-semibold">
                  {formatValue(analytics.total_views || 0)}
                </div>
                <div className="text-gray-400 text-xs mt-2 tracking-wide">
                  Views
                </div>
              </div>

              {/* Likes */}
              <div className="flex flex-col items-center">
                <div className="text-white text-lg font-semibold">
                  {formatValue(analytics.total_likes || 0)}
                </div>
                <div className="text-gray-400 text-xs mt-2 tracking-wide">
                  Likes
                </div>
              </div>

              {/* Comments */}
              <div className="flex flex-col items-center">
                <div className="text-white text-lg font-semibold">
                  {formatValue(analytics.total_comments || 0)}
                </div>
                <div className="text-gray-400 text-xs mt-2 tracking-wide">
                  Comments
                </div>
              </div>
            </div>
          ) : (
            /* Facebook and other social media metrics */
            <div className="grid grid-cols-4 gap-4">
              {/* Views/Impressions */}
              <div className="flex flex-col items-center">
                <div className="text-white text-lg font-semibold">
                  {formatValue(
                    analytics.post_impressions_unique || 
                    analytics.post_total_media_view_unique || 
                    analytics.impressions || 
                    analytics.views || 
                    0
                  )}
                </div>
                <div className="text-gray-400 text-xs mt-2 tracking-wide">
                  Views
                </div>
              </div>

              {/* Clicks */}
              <div className="flex flex-col items-center">
                <div className="text-white text-lg font-semibold">
                  {formatValue(
                    analytics.post_clicks_by_type || 
                    analytics.clicks || 
                    0
                  )}
                </div>
                <div className="text-gray-400 text-xs mt-2 tracking-wide">
                  Clicks
                </div>
              </div>

              {/* Reactions/Likes */}
              <div className="flex flex-col items-center">
                <div className="text-white text-lg font-semibold">
                  {formatValue(
                    analytics.post_reactions_by_type_total || 
                    analytics.reactions || 
                    analytics.likes || 
                    0
                  )}
                </div>
                <div className="text-gray-400 text-xs mt-2 tracking-wide">
                  Likes
                </div>
              </div>

              {/* Media Views */}
              <div className="flex flex-col items-center">
                <div className="text-white text-lg font-semibold">
                  {formatValue(
                    analytics.post_media_view || 
                    analytics.media_views || 
                    0
                  )}
                </div>
                <div className="text-gray-400 text-[10px] mt-2 tracking-wide">
                  Media
                </div>
              </div>
            </div>
          )
        ) : (
          /* No Data */
          <div className="text-center text-gray-500 text-sm py-4">
            No analytics available
          </div>
        )}
      </div>
    </div>
  );
};

export default PlatformAnalyticsCard;
